// src/Calculators/CommonComponents/Header.jsx
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, ChevronDown, X, User } from "lucide-react";
import 'bootstrap/dist/css/bootstrap.min.css';
import './Navbar.css';

const Navbar = ({ toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileDropdownOpen, setIsMobileDropdownOpen] = useState(false);

  // Check if user is logged in
  const isLoggedIn = !!localStorage.getItem("authToken");

  const isHomePage = location.pathname === '/' || location.pathname === '/home';

  const handleMarketplaceClick = (e) => {
    e.preventDefault();
    if (isHomePage) {
      const productsSection = document.getElementById('products');
      if (productsSection) {
        productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      navigate('/', { state: { scrollToProducts: true } });
    }
    setIsMobileMenuOpen(false);
  };

  const handleCalculatorClick = (path) => {
    setIsDropdownOpen(false);
    setIsMobileDropdownOpen(false);
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    setIsMobileDropdownOpen(false);
  };

  const toggleMobileDropdown = () => {
    setIsMobileDropdownOpen(!isMobileDropdownOpen);
  };

  // Logout handler
  const handleLogout = () => {
    localStorage.removeItem("authToken");
    setIsMobileMenuOpen(false);
    navigate("/login");
  };

  const calculatorCategories = [
    {
      title: "Investment & Savings",
      icon: "💰",
      calculators: [
        { name: "SIP Calculator", path: "/finance/sip" },
        { name: "Mutual Fund Calculator", path: "/finance/mutual" },
        { name: "Compound Interest", path: "/finance/compound" },
        { name: "Simple Interest", path: "/finance/simple" },
        { name: "Fixed Deposit (FD)", path: "/finance/fd" },
        { name: "Recurring Deposit (RD)", path: "/finance/rd" },
        { name: "PPF Calculator", path: "/finance/ppf" },
        { name: "NPS Calculator", path: "/finance/nps" },
        { name: "SSY Calculator", path: "/finance/ssy" },
        { name: "Retirement Corpus", path: "/finance/retirement" },
        { name: "Goal-Based Investment", path: "/finance/goal" },
      ]
    },
    {
      title: "Loans & Credit",
      icon: "🏠",
      calculators: [
        { name: "EMI Calculator", path: "/finance/emi" },
        { name: "Loan Amortization", path: "/finance/loan" },
        { name: "Loan Eligibility", path: "/finance/loanEligibility" },
        { name: "Prepayment Impact", path: "/finance/prepayment" },
        { name: "Balance Transfer", path: "/finance/balance" },
        { name: "Credit Card Interest", path: "/finance/credit" },
        { name: "Debt-to-Income Ratio", path: "/finance/debt" },
      ]
    },
    {
      title: "Business & ROI",
      icon: "📈",
      calculators: [
        { name: "ROI Calculator", path: "/finance/roi" },
        { name: "Break-Even Point", path: "/finance/break" },
        { name: "Profit Margin", path: "/finance/profit" },
        { name: "NPV Calculator", path: "/finance/npv" },
        { name: "IRR Calculator", path: "/finance/irr" },
        { name: "DCF Calculator", path: "/finance/dcf" },
      ]
    },
    {
      title: "Taxation",
      icon: "🧾",
      calculators: [
        { name: "Income Tax", path: "/finance/income" },
        { name: "GST Calculator", path: "/finance/GST" },
        { name: "HRA Exemption", path: "/finance/hra" },
        { name: "Gratuity Calculator", path: "/finance/gratuity" },
        { name: "Provident Fund (PF)", path: "/finance/pf" },
        { name: "TDS Calculator", path: "/finance/tds" },
        { name: "Capital Gains Tax", path: "/finance/capital" },
      ]
    },
    {
      title: "Daily Life & Personal",
      icon: "🧮",
      calculators: [
        { name: "Unit Converter", path: "/finance/unit" },
        { name: "Tip Calculator", path: "/finance/tip" },
        { name: "Discount Calculator", path: "/finance/discount" },
        { name: "Percentage Calculator", path: "/finance/percentage" },
        { name: "Age Calculator", path: "/finance/age" },
        { name: "Date Difference", path: "/finance/date" },
        { name: "Salary Breakup", path: "/finance/salary" },
        { name: "Currency Converter", path: "/finance/currency" },
      ]
    },
    {
      title: "Health & Fitness",
      icon: "🏥",
      calculators: [
        { name: "BMI Calculator", path: "/finance/bmi" },
        { name: "BMR Calculator", path: "/finance/bmr" },
        { name: "Calorie Intake", path: "/finance/calorie" },
        { name: "Ideal Weight", path: "/finance/ideal" },
        { name: "Body Fat %", path: "/finance/body" },
        { name: "Heart Rate Zone", path: "/finance/heart" },
        { name: "Pregnancy Due Date", path: "/finance/pregnancy" },
      ]
    },
    {
      title: "Business & Accounting",
      icon: "💼",
      calculators: [
        { name: "Invoice Calculator", path: "/finance/invoice" },
        { name: "Inventory Valuation", path: "/finance/inventory" },
        { name: "Depreciation", path: "/finance/depreciation" },
        { name: "Gross Profit / Net Profit", path: "/finance/gross" },
        { name: "Loan vs Lease", path: "/finance/loanvslease" },
        { name: "CAGR Calculator", path: "/finance/cagr" },
        { name: "EBITDA Calculator", path: "/finance/ebitda" },
      ]
    },
    {
      title: "Technical & Utility",
      icon: "🧰",
      calculators: [
        { name: "Percentage Change", path: "/finance/PercentageChange" },
        { name: "Fuel Cost Calculator", path: "/finance/fuel" },
        { name: "Electricity Bill Estimator", path: "/finance/electricity" },
        { name: "Time Duration", path: "/finance/time" },
        { name: "Savings Goal Timeline", path: "/finance/savings" },
        { name: "Currency Inflation Adjuster", path: "/finance/inflation" },
      ]
    },
  ];

  return (
    <nav className="custom-navbar">
      <div className="navbar-container">
        {/* Left: Hamburger + Logo + Links */}
        <div className="navbar-left">
          {toggleSidebar && (
            <button
              onClick={toggleSidebar}
              className="btn btn-link text-light p-0"
              aria-label="Toggle Sidebar"
            >
              <Menu size={24} />
            </button>
          )}

          <Link to="/" className="navbar-logo">
            <img src="/logo1.png" alt="Company Logo" className="company-logo" />
          </Link>

          {/* Desktop Links */}
          <ul className={`navbar-links ${isMobileMenuOpen ? 'mobile-menu-active' : ''}`}>
            <li>
              <a href="#products" onClick={handleMarketplaceClick} className="nav-link">
                Marketplace
              </a>
            </li>

            <li
              className={`nav-dropdown-container ${isMobileDropdownOpen ? 'mobile-dropdown-active' : ''}`}
              onMouseEnter={() => isHomePage && window.innerWidth > 991 && setIsDropdownOpen(true)}
              onMouseLeave={() => window.innerWidth > 991 && setIsDropdownOpen(false)}
            >
              {isHomePage ? (
                <div className="nav-link-with-dropdown">
                  <span 
                    className="nav-link"
                    onClick={() => window.innerWidth <= 991 && toggleMobileDropdown()}
                  >
                    Financial Calculators
                    <ChevronDown size={16} className="dropdown-icon" />
                  </span>

                  {((isDropdownOpen && window.innerWidth > 991) || (isMobileDropdownOpen && window.innerWidth <= 991)) && (
                    <div className="mega-dropdown">
                      <div className="dropdown-grid">
                        {calculatorCategories.map((category, idx) => (
                          <div key={idx} className="dropdown-column">
                            <div className="dropdown-category-title">
                              <span className="category-icon">{category.icon}</span>
                              {category.title}
                            </div>
                            <ul className="dropdown-items">
                              {category.calculators.map((calc, calcIdx) => (
                                <li key={calcIdx}>
                                  <button
                                    onClick={() => handleCalculatorClick(calc.path)}
                                    className="dropdown-item-btn"
                                  >
                                    {calc.name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link 
                  to="/finance/sip" 
                  className="nav-link"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Financial Calculators
                </Link>
              )}
            </li>

            <li>
            </li>
          </ul>
        </div>

        {/* Right: Mobile Menu Toggle + Conditional Auth Buttons */}
        <div className="navbar-right">
          <button
            onClick={toggleMobileMenu}
            className="mobile-menu-toggle"
            aria-label="Toggle Mobile Menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          
          {/* Conditional Rendering Based on Authentication */}
          {isLoggedIn ? (
            <>
              <Link to="/profile" className="btn-profile">
                <User size={18} />
                <span>Profile</span>
              </Link>
              <button onClick={handleLogout} className="btn-logout">
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn-login">Log In</Link>
              <Link to="/signup" className="btn-signup">Sign Up</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;